
import { Button, Card, Col, DatePicker, Form, Input, Modal, Row, Select, Typography } from 'antd';
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createValidityEntry, fetchValidityEntry, updateDefaultValidityId } from '../../redux/rtk/features/leavePolicy/leavePolicySlice';
import getCompanyIdFromToken from '../../utils/getCompanyIdFromToken';
import PageTitle from "../page-header/PageHeader";
import AddLeavePolicy from "./AddLeavePolicy";
import LeavePolicyTable from './LeavePolicyTable';

const { Title } = Typography;
dayjs.extend(utc);
dayjs.extend(timezone);

const LeavePolicyPage = () => {
  const [isValidityModalVisible, setIsValidityModalVisible] = useState(false);
  const [isDefaultValidityModalVisible, setIsDefaultValidityModalVisible] = useState(false);

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const companyId = getCompanyIdFromToken();
  const validityData = useSelector((state) => state.leavePolicy.validity);
  // console.log("validityData", validityData);


  useEffect(() => {
    console.log("dispatching")
    dispatch(fetchValidityEntry());
  }, [dispatch]);

  useEffect(() => {
    // Pre-fill form with existing data if available
    if (validityData) {
      form.setFieldsValue({
        validFrom: validityData.validFrom ? dayjs(validityData.validFrom) : null,
        validTo: validityData.validTo ? dayjs(validityData.validTo) : null,
      });
    }
  }, [validityData, form]);

  const formatDate = (date) => {
    return dayjs(date)
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  };


  const onFinish = async (values) => {
    const FormData = {
      ...values,
      validFrom: formatDate(values.validFrom),
      validTo: formatDate(values.validTo),
      company_id: companyId,
    };

    setLoader(true);
    const resp = await dispatch(createValidityEntry(FormData));
    if (resp.payload.message === "success") {
      setLoader(false);
      form.resetFields();
      setIsValidityModalVisible(false);
    } else {
      setLoader(false);
    }
  };

  const onDefaultValiditySubmit = async () => {
    const validityId = form.getFieldValue("validity_id");
    console.log("validityId", validityId);

    const response = dispatch(updateDefaultValidityId({ company_id: companyId, values: { validity_id: validityId } }));

    console.log("Response Payload:", { companyId, validityId });
    if (response.payload.message === "success") {
      setLoader(false);
      form.resetFields();
      setIsDefaultValidityModalVisible(false);
    } else {
      setLoader(false);
    }
  };


  const showValidityModal = () => {
    setIsValidityModalVisible(true);
  };

  const handleValidityCancel = () => {
    setIsValidityModalVisible(false);
  };

  const showDefaultValidityModal = () => {
    setIsDefaultValidityModalVisible(true);
  };

  const handleDefaultValidityCancel = () => {
    setIsDefaultValidityModalVisible(false);
  };


  return (
    <>
      <PageTitle title='Back' />
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item>
          <Button
            type="primary"
            onClick={showValidityModal}
            style={{ marginTop: '8px' }}
          >
            Set Validity
          </Button>
          <Button
            type="primary"
            onClick={showDefaultValidityModal}
            style={{ marginTop: '8px', marginLeft: '10px' }}
          >
            Set Default Validity
          </Button>
        </Form.Item>

        <Modal
          title="Set Validity"
          visible={isValidityModalVisible}
          onOk={() => form.submit()} // Submit the form when the "Confirm" button is clicked
          onCancel={handleValidityCancel}
          confirmLoading={loader}
          footer={[
            <Button key="cancel" onClick={handleValidityCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" onClick={() => form.submit()}>
              Confirm
            </Button>
          ]}
        >
          <Form.Item label="Validity" style={{ marginBottom: 0 }}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please Enter Name!' }]}
            >
              <Input placeholder="Enter Name" />
            </Form.Item>
            <Form.Item
              name="validFrom"
              label="From Date"
              rules={[{ required: true, message: 'Please select the start date!' }]}
              style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
            >
              <DatePicker placeholder="Select start date" />
            </Form.Item>
            <span style={{ display: 'inline-block', width: '16px', textAlign: 'center' }}></span>
            <Form.Item
              name="validTo"
              label="To Date"
              rules={[{ required: true, message: 'Please select the end date!' }]}
              style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
            >
              <DatePicker placeholder="Select end date" />
            </Form.Item>
          </Form.Item>
        </Modal>

        <Modal
          title="Set Default Validity"
          visible={isDefaultValidityModalVisible}
          onCancel={handleDefaultValidityCancel}
          footer={[
            <Button key="cancel" onClick={handleDefaultValidityCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" loading={loader} onClick={onDefaultValiditySubmit}>
              Confirm
            </Button>,
          ]}
        >
          <Form.Item
            name="validity_id"
            label="Select Validity"
            rules={[{ required: true, message: 'Please select validity!' }]}
          >
              <Select placeholder="Select Validity" loading={!validityData}>
    {validityData?.map((entry) => {
      const formattedValidFrom = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).format(new Date(entry.validFrom));

      const formattedValidTo = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).format(new Date(entry.validTo));

      return (
        <Select.Option key={entry.id} value={entry.id}>
          {entry.name} ({formattedValidFrom} to {formattedValidTo})
        </Select.Option>
      );
    })}
  </Select>
          </Form.Item>

        </Modal>

      </Form>
      <Row gutter={16}>
        <Col span={8}>
          <Card title="Create or Edit Leave Policy">
            <AddLeavePolicy />
          </Card>
        </Col>
        <Col span={16}>
          <Card title="Leave Policies">
            <LeavePolicyTable />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default LeavePolicyPage;
